import { AxiosResponse } from "axios";

const downloadRuneMap: Record<string, string> = {
  Ä: "Ae",
  Ö: "Oe",
  Ü: "Ue",
  ä: "ae",
  ö: "oe",
  ü: "ue",
  ß: "ss",
  À: "A",
  Á: "A",
  Â: "A",
  Ã: "A",
  à: "a",
  á: "a",
  â: "a",
  ã: "a",
  É: "E",
  È: "E",
  Ê: "E",
  Ë: "E",
  é: "e",
  è: "e",
  ê: "e",
  ë: "e",
  Í: "I",
  Ì: "I",
  Î: "I",
  Ï: "I",
  í: "i",
  ì: "i",
  î: "i",
  ï: "i",
  Ó: "O",
  Ò: "O",
  Ô: "O",
  Õ: "O",
  ó: "o",
  ò: "o",
  ô: "o",
  õ: "o",
  Ú: "U",
  Ù: "U",
  Û: "U",
  ú: "u",
  ù: "u",
  û: "u",
  Ñ: "N",
  ñ: "n",
  Ç: "C",
  ç: "c",
};

function sanitizeFilenamePart(value: string, fallback: string): string {
  const mapped = Array.from(value.trim())
    .map((char) => downloadRuneMap[char] ?? char)
    .join("");
  let out = "";
  let prevHyphen = false;
  for (const char of mapped) {
    if (
      char === " " ||
      char === "\t" ||
      char === "\n" ||
      char === "\r" ||
      char === "_" ||
      char === "/" ||
      char === "\\"
    ) {
      if (out.length > 0 && !prevHyphen) {
        out += "-";
        prevHyphen = true;
      }
      continue;
    }
    const code = char.charCodeAt(0);
    if (code < 32 || code > 126 || ':*?"<>|'.includes(char)) {
      continue;
    }
    out += char;
    prevHyphen = char === "-";
  }
  out = out.replace(/^[-.]+|[-.]+$/g, "");
  if (!out) {
    return fallback;
  }
  const trimmed = Array.from(out).slice(0, 80).join("").replace(/^[-.]+|[-.]+$/g, "");
  return trimmed || fallback;
}

/** dd-mm-yy-ProjectName-ModuleName-searchanalyzer-io.xlsx */
function downloadFilename(projectName: string, moduleName: string, ext: string): string {
  const now = new Date();
  const dd = String(now.getDate()).padStart(2, "0");
  const mm = String(now.getMonth() + 1).padStart(2, "0");
  const yy = String(now.getFullYear()).slice(-2);
  const extension = ext.startsWith(".") ? ext.toLowerCase() : `.${ext.toLowerCase()}`;
  const project = sanitizeFilenamePart(projectName, "Project");
  const modulePart = sanitizeFilenamePart(moduleName, "Export");
  return `${dd}-${mm}-${yy}-${project}-${modulePart}-searchanalyzer-io${extension}`;
}

function filenameFromContentDisposition(contentHeader: string | undefined): string {
  if (!contentHeader) {
    return "download";
  }
  const star = /filename\*\s*=\s*(?:UTF-8'')?([^;]+)/i.exec(contentHeader);
  if (star) {
    const raw = star[1].trim().replace(/^"(.*)"$/, "$1");
    try {
      return decodeURIComponent(raw);
    } catch {
      return raw;
    }
  }
  const quoted = /filename\s*=\s*"((?:\\.|[^"])*)"/i.exec(contentHeader);
  if (quoted) {
    return quoted[1].replace(/\\"/g, '"').replace(/\\\\/g, "\\");
  }
  const plain = /filename\s*=\s*([^;]+)/i.exec(contentHeader);
  if (plain) {
    return plain[1].trim();
  }
  return "download";
}

function fileDownload(response: AxiosResponse<any>) {
  const filename = filenameFromContentDisposition(response.headers["content-disposition"]);
  const url = window.URL.createObjectURL(
    new Blob([response.data], {
      type: "application/octet-stream",
    })
  );
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  // Without this the blob stays in memory until the page is reloaded.
  window.URL.revokeObjectURL(url);
}

type SaveWritable = {
  getWriter: () => {
    write: (chunk: Uint8Array) => Promise<void>;
    close: () => Promise<void>;
    abort: () => Promise<void>;
  };
};

type SaveFileHandle = {
  createWritable: () => Promise<SaveWritable>;
};

type WindowWithSavePicker = Window & {
  showSaveFilePicker?: (options: { suggestedName?: string }) => Promise<SaveFileHandle>;
};

export type AuthorizedDownload = "picker" | "browser" | "cancelled";

type AuthorizedFileOptions = {
  url: string;
  headers: Record<string, string>;
  suggestedName: string;
  signal?: AbortSignal;
  onProgress?: (loaded: number) => void;
  /** Used when the browser cannot stream into a chosen file. */
  browserDownload?: () => Promise<void>;
};

type CSVCount = { quotes: boolean; newlines: number; pendingQuote: boolean };

function countCSVNewlines(chunk: Uint8Array, state: CSVCount) {
  let index = 0;
  if (state.pendingQuote) {
    state.pendingQuote = false;
    if (chunk[0] === 0x22) {
      index = 1;
    } else {
      state.quotes = false;
    }
  }
  for (; index < chunk.length; index++) {
    const byte = chunk[index];
    if (byte === 0x22) {
      if (state.quotes) {
        if (index + 1 >= chunk.length) {
          state.pendingQuote = true;
          continue;
        }
        if (chunk[index + 1] === 0x22) {
          index++;
          continue;
        }
        state.quotes = false;
        continue;
      }
      state.quotes = true;
      continue;
    }
    if (!state.quotes && byte === 0x0a) {
      state.newlines++;
    }
  }
}

function rememberTail(previous: Uint8Array, chunk: Uint8Array, size: number): Uint8Array {
  if (chunk.length >= size) {
    return chunk.slice(chunk.length - size);
  }
  const merged = new Uint8Array(previous.length + chunk.length);
  merged.set(previous);
  merged.set(chunk, previous.length);
  if (merged.length <= size) {
    return merged;
  }
  return merged.slice(merged.length - size);
}

function hasZipEnding(tail: Uint8Array): boolean {
  if (tail.length < 22) {
    return false;
  }
  const start = tail.length - 22;
  return tail[start] === 0x50 && tail[start + 1] === 0x4b && tail[start + 2] === 0x05 && tail[start + 3] === 0x06;
}

async function failDownload(response: Response): Promise<never> {
  let message = "download failed";
  try {
    const body = (await response.json()) as { message?: unknown };
    if (typeof body.message === "string" && body.message.trim() !== "") {
      message = body.message;
    }
  } catch {
    // The error body was not JSON.
  }
  throw new Error(message);
}

async function writeResponseBody(
  response: Response,
  writable: SaveWritable,
  onProgress?: (loaded: number) => void
) {
  if (!response.body) {
    throw new Error("download failed");
  }
  const csv = (response.headers.get("content-type") || "").includes("text/csv");
  const expected = Number(response.headers.get("x-export-rows"));
  if (csv && !Number.isInteger(expected)) {
    throw new Error("download failed");
  }
  const reader = response.body.getReader();
  const writer = writable.getWriter();
  const csvState: CSVCount = { quotes: false, newlines: 0, pendingQuote: false };
  let tail = new Uint8Array(0);
  let loaded = 0;
  try {
    while (true) {
      const step = await reader.read();
      if (step.done) {
        break;
      }
      if (!step.value) {
        continue;
      }
      await writer.write(step.value);
      loaded += step.value.byteLength;
      if (onProgress) {
        onProgress(loaded);
      }
      if (csv) {
        countCSVNewlines(step.value, csvState);
      } else {
        tail = rememberTail(tail, step.value, 22);
      }
    }
    if (csvState.pendingQuote) {
      csvState.quotes = false;
    }
    if (csv && csvState.newlines - 1 !== expected) {
      throw new Error("download failed");
    }
    if (!csv && !hasZipEnding(tail)) {
      throw new Error("download failed");
    }
    await writer.close();
  } catch (error) {
    await writer.abort().catch(() => undefined);
    throw error;
  }
}

async function startBrowserDownload(options: AuthorizedFileOptions): Promise<AuthorizedDownload> {
  if (!options.browserDownload) {
    throw new Error("download failed");
  }
  await options.browserDownload();
  return "browser";
}

function isUserAbort(error: unknown): boolean {
  return (
    typeof error === "object" &&
    error !== null &&
    "name" in error &&
    (error as { name?: string }).name === "AbortError"
  );
}

/**
 * Saves a response without keeping the whole file in the tab.
 * The file picker streams each chunk to disk. Without that picker the caller
 * starts a normal browser download, which also stays out of the tab.
 * A short file is rejected: CSV must contain the announced row count, and
 * XLSX must end like a zip.
 */
async function downloadAuthorizedFile(options: AuthorizedFileOptions): Promise<AuthorizedDownload> {
  const picker = (window as WindowWithSavePicker).showSaveFilePicker;
  if (typeof picker !== "function") {
    return startBrowserDownload(options);
  }
  let handle: SaveFileHandle;
  try {
    // Must run in the click turn, before any other await, or the browser
    // rejects the picker.
    handle = await picker({ suggestedName: options.suggestedName });
  } catch (error) {
    if (isUserAbort(error)) {
      return "cancelled";
    }
    return startBrowserDownload(options);
  }
  try {
    const response = await fetch(options.url, { headers: options.headers, signal: options.signal });
    if (!response.ok) {
      await failDownload(response);
    }
    const writable = await handle.createWritable();
    await writeResponseBody(response, writable, options.onProgress);
  } catch (error) {
    if (isUserAbort(error)) {
      return "cancelled";
    }
    throw error;
  }
  return "picker";
}

export { downloadAuthorizedFile, downloadFilename, fileDownload, filenameFromContentDisposition };
